/* eslint-disable react/sort-comp */
import React from 'react';
import Media from 'react-media';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';

import type {
  IResponsiveSizeBreakpoint,
  IWithResponsiveSizeInjectedProps,
  IWithResponsiveSizeProps,
  IWithResponsiveSizeState,
} from './types';

export const withResponsiveSize = <P extends IWithResponsiveSizeInjectedProps>(
  WrappedComponent: React.ComponentType<P>,
  sizes: IResponsiveSizeBreakpoint[]
): React.ComponentType<IWithResponsiveSizeProps<P>> => {
  type WithResponsiveSizeProps = IWithResponsiveSizeProps<P>;

  class WithResponsiveSize extends React.Component <WithResponsiveSizeProps, IWithResponsiveSizeState> {
    private queries: IResponsiveSizeBreakpoint[];
    private smallestSizeName: string;

    constructor(props: WithResponsiveSizeProps) {
      super(props);

      this.state = {};

      // sort descending by minWidth
      const sortedSizes = sizes.sort((a, b) => b.minWidth - a.minWidth);

      // The smallest does not need a query since we can assumed we are in the smallest if
      // all other states are false.
      this.queries = sortedSizes.slice(0, -1);
      this.smallestSizeName = sortedSizes[sortedSizes.length - 1].name;

      for (let i = 0; i < this.queries.length; i += 1) {
        const state = this.state as IWithResponsiveSizeState;
        state[this.queries[i].name] = false;
      }
    }

    // eslint-disable-next-line react/sort-comp
    getCurrentSize(): string {
      // Queries are sorted in descending order by minWdith, so take the first one that is true
      for (let i = 0; i < this.queries.length; i += 1) {
        if (this.state[this.queries[i].name] === true) {
          return this.queries[i].name;
        }
      }

      // If all others are false, that means we are at the smallest size
      return this.smallestSizeName;
    }

    handleChange(name: string, match: boolean): void {
      const newState: IWithResponsiveSizeState = {};
      newState[name] = match;
      this.setState(newState);
    }

    render() {
      const { offset = 0, ...wrappedComponentProps } = this.props;

      return (
        <>
          {this.queries.map(query => (
            <Media
              key={`${query.name}${offset}`}
              query={`(min-width: ${query.minWidth + offset}px)`}
              onChange={matches => this.handleChange(query.name, matches)}
            />
          ))}

          <WrappedComponent
            responsiveSize={this.getCurrentSize()}
            {...(wrappedComponentProps as P)}
          />
        </>
      );
    }
  }

  const WithResponsiveSizeComponent = Object.assign(WithResponsiveSize, {
    displayName: `withResponsiveSize(${getDisplayName(WrappedComponent)})`,
  });

  hoistNonReactStatics(WithResponsiveSizeComponent, WrappedComponent);

  return WithResponsiveSizeComponent;
};
